// One player for the whole app, like the app's playback bar (docs/web-designs screen 2): it keeps
// playing while another chat is open, shows the current section on top, and marks someone else's
// voice message as heard once 80 % of it played (POST /v1/messages/{id}/heard).

import { useSyncExternalStore } from "react";

export interface Track {
  messageId: string;
  conversationId: string;
  senderName: string;
  durationMs: number;
  /** Section titles with their starts, for the bar. */
  sections: { startMs: number; title: string }[];
  /** Someone else's: report "heard" at 80 %. */
  reportHeard: boolean;
}

export interface PlayerState {
  track: Track | null;
  playing: boolean;
  positionMs: number;
  rate: number;
}

const RATES = [1, 1.5, 2];

export class Player {
  private audio: HTMLAudioElement | null = null;
  private state: PlayerState = { track: null, playing: false, positionMs: 0, rate: 1 };
  private readonly listeners = new Set<() => void>();
  private readonly heard = new Set<string>();
  private readonly onHeard: (track: Track) => void;

  constructor(onHeard: (track: Track) => void) {
    this.onHeard = onHeard;
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getState = () => this.state;

  /** Plays [url] (the decrypted Ogg/Opus) from [fromMs]; the same track again only seeks. */
  async play(track: Track, url: string, fromMs = 0): Promise<void> {
    const audio = this.element();
    if (this.state.track?.messageId !== track.messageId) {
      audio.src = url;
      this.set({ track, positionMs: fromMs });
    }
    audio.currentTime = fromMs / 1000;
    audio.playbackRate = this.state.rate;
    await audio.play();
  }

  toggle() {
    const audio = this.audio;
    if (!audio || !this.state.track) return;
    if (audio.paused) void audio.play();
    else audio.pause();
  }

  seek(ms: number) {
    if (this.audio) this.audio.currentTime = ms / 1000;
  }

  skip(deltaMs: number) {
    if (this.audio) this.audio.currentTime = Math.max(0, this.audio.currentTime + deltaMs / 1000);
  }

  nextRate() {
    const rate = RATES[(RATES.indexOf(this.state.rate) + 1) % RATES.length]!;
    if (this.audio) this.audio.playbackRate = rate;
    this.set({ rate });
  }

  stop() {
    this.audio?.pause();
    this.set({ track: null, playing: false, positionMs: 0 });
  }

  private element(): HTMLAudioElement {
    if (this.audio) return this.audio;
    const audio = new Audio();
    audio.preload = "auto";
    audio.addEventListener("play", () => this.set({ playing: true }));
    audio.addEventListener("pause", () => this.set({ playing: false }));
    audio.addEventListener("ended", () => this.set({ playing: false }));
    audio.addEventListener("timeupdate", () => {
      const positionMs = audio.currentTime * 1000;
      this.set({ positionMs });
      const track = this.state.track;
      if (track?.reportHeard && !this.heard.has(track.messageId) && positionMs >= track.durationMs * 0.8) {
        this.heard.add(track.messageId);
        this.onHeard(track);
      }
    });
    this.audio = audio;
    return audio;
  }

  private set(patch: Partial<PlayerState>) {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }
}

export function usePlayer(player: Player): PlayerState {
  return useSyncExternalStore(player.subscribe, player.getState);
}
